import { cx } from '@emotion/css';
import { Stack } from '@mui/material';
import { isString } from 'lodash';
import { isValidElement } from 'react';

import { Card } from '@/components/layout/Card/Card';
import { Markdown } from '@/components/Markdown/Markdown';
import { getIdSafeString } from '@/utils/formatting/strings';

import {
  TILE_DEFAULT_FONT_SIZE_VARIANT,
  TILE_DEFAULT_VARIANT,
} from './constants';
import { DefaultWarningIndicator } from './DefaultWarningIndicator';
import { useTileClasses } from './styles';
import { TileProps } from './Tile';
import {
  getIconForKind,
  getIconForVariant,
  SemanticTileKind,
} from './Tile.utils';
import { TileFooterLabel } from './TileFooterLabel';
import {
  TileThemedTypography,
  TileThemedTypographyClamped,
} from './TileThemedTypography';
import { TileVariant } from './types';

type TileCardProps = TileProps &
  ReturnType<typeof useTileClasses> & {
    variant: Exclude<TileProps['variant'], typeof TileVariant.Pixel>;
    kind?: SemanticTileKind;
  };

export function TileCard({
  variantClasses,
  variant = TILE_DEFAULT_VARIANT,
  customColor,
  fontSizeVariant = TILE_DEFAULT_FONT_SIZE_VARIANT,
  classes,
  isGrouped,
  isSelected,
  hover,
  lineOne,
  lineTwo,
  lineTwoVariant = 'h2light',
  lineThree,
  notes,
  children,
  slots,
  footerLabel,
  isIconVariant,
  iconShape,
  tileEmphasis,
  testId,
  kind,
  warningIndicator,
  onDoubleClick,
  showFullText,
}: TileCardProps) {
  // A full-text tile shows all of each line, so the tile height follows the
  // text. Other tiles clamp each line. Without `lines`, the typography renders
  // as an inline span that takes the taller line height of its parent. Keep
  // the block display and the line breaks that the clamp applies.
  const lineProps = (lines: number) =>
    showFullText
      ? { sx: { display: 'block', whiteSpace: 'pre-wrap' } as const }
      : { lines };

  let FooterLabel = null;
  if (slots?.FooterLabel) {
    FooterLabel = slots.FooterLabel;
  } else if (footerLabel) {
    FooterLabel = (
      <TileFooterLabel
        tileVariant={variant}
        tileCustomColor={customColor}
        fontSizeVariant={fontSizeVariant}
      >
        {footerLabel}
      </TileFooterLabel>
    );
  }

  const Icon = isIconVariant
    ? (slots?.Icon ?? getIconForKind(kind) ?? getIconForVariant(variant))
    : null;

  // Icon tiles render their text labels next to/below the colored icon rather
  // than on top of the emphasized body, so emphasis-driven text contrast (which
  // can flip text to white for high-contrast bodies) doesn't apply here.
  // Mirrors the `emphasisForBody` carve-out in styles.ts.
  const emphasisForText = isIconVariant ? undefined : tileEmphasis;

  const isGroupedOrSelected = isGrouped || isSelected;

  // Resolve the warning indicator - use default if boolean true is passed
  const resolvedWarningIndicator =
    warningIndicator === true ? (
      <DefaultWarningIndicator isIconVariant={isIconVariant ?? false} />
    ) : (
      warningIndicator
    );

  return (
    <Card
      variant="transparent"
      data-testid={
        isString(lineOne) && !testId
          ? // Backwards compat
            `TileCard-${getIdSafeString(lineOne)}`
          : testId
      }
      className={cx(
        variantClasses[variant].root,
        classes.root,
        hover && variantClasses[variant].hoverRoot,
        hover && classes.hoverRoot,
        isGroupedOrSelected && variantClasses[variant].selectedRoot,
        isGroupedOrSelected && classes.selectedRoot,
        !!resolvedWarningIndicator && classes.warningOverflow // The warning indicator must overflow the tile
      )}
      onDoubleClick={onDoubleClick}
    >
      <div
        className={cx(
          variantClasses[variant].innerRoot,
          classes.innerRoot,
          isGroupedOrSelected && variantClasses[variant].innerRootSelected,
          isGroupedOrSelected && classes.innerRootSelected
        )}
      >
        {Icon && !iconShape && (
          // Legacy path (no explicit iconShape): single-div structure where
          // the warning indicator is a child of the icon pill and positioned
          // relative to it. Used by the dispositions single-entity diagram,
          // which never sets iconShape.
          <div
            className={cx(
              variantClasses[variant].icon,
              classes.icon,
              'Tile-Icon'
            )}
          >
            {Icon}
            {resolvedWarningIndicator && isIconVariant && (
              <div className={cx(classes.warningIndicator)}>
                {resolvedWarningIndicator}
              </div>
            )}
          </div>
        )}
        {Icon && iconShape && (
          // Shape-enabled path: the icon clip-path would clip the warning
          // indicator, so we wrap the icon in a non-clipping positioning
          // context and render the indicator as a sibling.
          <div
            className={cx(
              classes.iconWrapper,
              !!resolvedWarningIndicator && classes.warningOverflow
            )}
          >
            <div
              className={cx(
                variantClasses[variant].icon,
                classes.icon,
                'Tile-Icon'
              )}
            >
              {Icon}
            </div>
            {resolvedWarningIndicator && isIconVariant && (
              <div className={cx(classes.warningIndicator)}>
                {resolvedWarningIndicator}
              </div>
            )}
          </div>
        )}

        <div
          className={cx(
            variantClasses[variant].linesWrapper,
            classes.linesWrapper
          )}
        >
          {lineOne && (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              className={cx(variantClasses[variant].lineOne, classes.lineOne)}
            >
              {resolvedWarningIndicator &&
                !isIconVariant &&
                resolvedWarningIndicator}
              <TileThemedTypographyClamped
                variant="title-s"
                tileVariant={variant}
                tileCustomColor={customColor}
                tileEmphasis={emphasisForText}
                fontSizeVariant={fontSizeVariant}
                {...lineProps(isIconVariant ? 3 : 2)}
              >
                {lineOne}
              </TileThemedTypographyClamped>
            </Stack>
          )}
          {lineTwo && (
            <TileThemedTypographyClamped
              {...lineProps(2)}
              variant={lineTwoVariant}
              tileVariant={variant}
              tileCustomColor={customColor}
              tileEmphasis={emphasisForText}
              fontSizeVariant={fontSizeVariant}
              className={cx(variantClasses[variant].lineTwo, classes.lineTwo)}
            >
              {lineTwo}
            </TileThemedTypographyClamped>
          )}
          {lineThree && (
            <TileThemedTypographyClamped
              {...lineProps(2)}
              variant="body-xs"
              tileVariant={variant}
              tileCustomColor={customColor}
              tileEmphasis={emphasisForText}
              fontSizeVariant={fontSizeVariant}
              className={cx(
                variantClasses[variant].lineThree,
                classes.lineThree
              )}
            >
              {lineThree}
            </TileThemedTypographyClamped>
          )}
          {/* Notes render below the label for both tile variants. */}
          {notes && (
            <>
              {isValidElement(notes) ? (
                notes
              ) : (
                <Markdown
                  markdown={notes as string}
                  preserveNewLines
                  EditorInner={({ children }) => (
                    <TileThemedTypography
                      variant="body-xs"
                      tileVariant={variant}
                      tileCustomColor={customColor}
                      tileEmphasis={emphasisForText}
                      fontSizeVariant={fontSizeVariant}
                      className={cx(
                        variantClasses[variant].notes,
                        classes.notes
                      )}
                    >
                      {children}
                    </TileThemedTypography>
                  )}
                />
              )}
            </>
          )}
        </div>
        {isIconVariant && (slots?.FooterLabel || footerLabel) && (
          <TileThemedTypographyClamped
            lines={2}
            variant="body-xs"
            tileVariant={variant}
            tileCustomColor={customColor}
            fontSizeVariant={fontSizeVariant}
            sx={{ textAlign: 'center' }}
          >
            {slots?.FooterLabel || footerLabel}
          </TileThemedTypographyClamped>
        )}
      </div>
      {!isIconVariant && children}
      {!isIconVariant && FooterLabel && (
        <div
          className={cx(
            variantClasses[variant].footerWrapper,
            classes.footerWrapper,
            isGroupedOrSelected &&
              variantClasses[variant].footerWrapperSelected,
            isGroupedOrSelected && classes.footerWrapperSelected
          )}
        >
          {FooterLabel}
        </div>
      )}
    </Card>
  );
}
